import {debounce} from 'lodash';
import {Box, SxProps} from '@mui/material';
import React, {useRef, useState, useEffect, useCallback} from 'react';
import {ReactComponent as DropDownIcon} from '../../../assests/svg/arrowupicon.svg';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {t} from 'i18next';

interface ResizableDocListProps {
  minHeight?: number;
  maxHeight?: number;
  children: React.ReactNode;
  searchInputRef?: React.RefObject<HTMLElement>;
  containerRef?: React.RefObject<HTMLElement>;
  errorBannerRef?: React.RefObject<HTMLElement>;
  parentSxProps?: SxProps;
  updateHeightsRef?: React.MutableRefObject<(() => void) | null>;
}

const ResizableDocList: React.FC<ResizableDocListProps> = ({
  minHeight: propMinHeight,
  maxHeight: propMaxHeight,
  searchInputRef,
  containerRef,
  errorBannerRef,
  parentSxProps,
  updateHeightsRef,
  children,
}) => {
  const MARGIN = 40;
  const DEFAULT_INPUT_HEIGHT = 150;
  const MIN_DOC_LIST_HEIGHT = 50;
  const HEADER_HEIGHT = 72;
  const CONTAINER_HEIGHT =
    containerRef?.current?.getBoundingClientRect().height ||
    2 * window.innerHeight;
  const [minHeight, setMinHeight] = useState(MIN_DOC_LIST_HEIGHT);
  const [maxHeight, setMaxHeight] = useState(
    propMaxHeight || CONTAINER_HEIGHT - HEADER_HEIGHT
  );
  const [height, setHeight] = useState(MIN_DOC_LIST_HEIGHT);
  const userSetHeight = useRef<number | null>(null);
  const lastSearchInputHeight = useRef<number | null>(null);
  const lastErrorBannerHeight = useRef<number | null>(null);
  const lastMaxHeight = useRef<number | null>(null);
  const lastMinHeight = useRef<number | null>(null);
  const lastComputedHeight = useRef<number | null>(null);

  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const hasDragged = useRef(false);
  const dragThreshold = 5;
  const {trackGAEvent} = useAnalytics();

  const updateHeights = useCallback(
    debounce(() => {
      if (isDragging.current) return;
      if (!searchInputRef?.current) {
        return;
      }
      const searchInputRect = searchInputRef?.current?.getBoundingClientRect();
      const errorBannerRect = errorBannerRef?.current?.getBoundingClientRect();
      const scrollY = window.scrollY;
      const searchInputHeight = searchInputRect?.height || DEFAULT_INPUT_HEIGHT;
      const searchInputTop = searchInputRect?.top || 0;
      const searchInputBottom = searchInputTop + searchInputHeight + scrollY;
      const errorBannerHeight = errorBannerRect?.height || 0;

      const containerHeight =
        containerRef?.current?.getBoundingClientRect().height ||
        2 * window.innerHeight;
      const newMaxHeight = propMaxHeight || containerHeight - HEADER_HEIGHT;
      if (lastMaxHeight.current !== newMaxHeight) {
        lastMaxHeight.current = newMaxHeight;
        setMaxHeight(newMaxHeight);
      }

      const availableHeight = containerHeight - searchInputBottom + MARGIN;
      const newMinHeight = Math.max(MIN_DOC_LIST_HEIGHT, availableHeight);
      if (lastMinHeight.current !== newMinHeight) {
        lastMinHeight.current = newMinHeight;
        setMinHeight(newMinHeight);
      }

      const hasRefSizeChanged =
        lastSearchInputHeight.current !== searchInputHeight ||
        lastErrorBannerHeight.current !== errorBannerHeight;

      let nextHeight: number;
      if (hasRefSizeChanged) {
        nextHeight = Math.min(
          newMaxHeight,
          Math.max(newMinHeight, availableHeight)
        );
        userSetHeight.current = null;
      } else if (userSetHeight.current !== null) {
        nextHeight = Math.min(
          newMaxHeight,
          Math.max(newMinHeight, userSetHeight.current)
        );
      } else {
        nextHeight = Math.min(
          newMaxHeight,
          Math.max(newMinHeight, availableHeight)
        );
      }

      if (lastComputedHeight.current !== nextHeight) {
        lastComputedHeight.current = nextHeight;
        setHeight(nextHeight);
      }
      lastSearchInputHeight.current = searchInputHeight;
      lastErrorBannerHeight.current = errorBannerHeight;
    }, 100),

    [propMinHeight, propMaxHeight, searchInputRef, containerRef, errorBannerRef]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      updateHeights();
    }, 100);
    window.addEventListener('resize', updateHeights);
    const resizeObserver = new ResizeObserver(() => {
      updateHeights();
    });
    const observedElements: HTMLElement[] = [];
    if (searchInputRef?.current) {
      resizeObserver.observe(searchInputRef.current);
      observedElements.push(searchInputRef.current);
    }
    if (errorBannerRef?.current) {
      resizeObserver.observe(errorBannerRef.current);
      observedElements.push(errorBannerRef.current);
    }
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateHeights);
      observedElements.forEach((element) => {
        resizeObserver.unobserve(element);
      });
      resizeObserver.disconnect();
    };
  }, [updateHeights, searchInputRef, errorBannerRef, containerRef]);

  useEffect(() => {
    if (updateHeightsRef) {
      updateHeightsRef.current = updateHeights;
    }
  }, [updateHeightsRef, updateHeights]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isDragging.current = true;
    hasDragged.current = false;
    startY.current = e.clientY;
    startHeight.current = height;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.current) return;
    const dy = e.clientY - startY.current;
    if (Math.abs(dy) > dragThreshold) {
      hasDragged.current = true;
    }
    const newHeight = Math.min(
      maxHeight,
      Math.max(minHeight, startHeight.current - dy)
    );
    setHeight(newHeight);
    userSetHeight.current = newHeight;
  };

  const handleMouseUp = () => {
    if (isDragging.current && hasDragged.current) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.indexSearch.resizeSearchBox'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.indexSearch.draggedToResizeSearchBox'),
        },
      });
    }
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleClick = () => {
    if (!hasDragged.current) {
      const isExpanding = height < maxHeight;
      const newHeight = height < maxHeight ? maxHeight : minHeight;
      setHeight(newHeight);
      userSetHeight.current = newHeight;

      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: !isExpanding
            ? t('gaTracking.indexSearch.expandSearchBox')
            : t('gaTracking.indexSearch.collapseSearchBox'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.BODY,
          action: !isExpanding
            ? t('gaTracking.indexSearch.expandedSearchBox')
            : t('gaTracking.indexSearch.collapsedSearchBox'),
        },
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleClick();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const step = 10;
      const newHeight = Math.min(
        maxHeight,
        Math.max(minHeight, height + (e.key === 'ArrowUp' ? -step : step))
      );
      setHeight(newHeight);
      userSetHeight.current = newHeight;
    }
  };

  return (
    <Box
      data-testid="resizable-doclist-container"
      className="resizable-doclist-container"
      sx={{
        height: `${height}px`,
        minHeight: `${minHeight}px`,
        maxHeight: `${maxHeight}px`,
        transition: isDragging.current ? 'none' : 'height 0.25s ease',
        ...parentSxProps,
      }}
    >
      <Box
        data-testid="resizable-doclist-bar-box"
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className="resizable-doclist-bar-box"
      >
        {height === maxHeight ? (
          <div className="resizable-doclist-icon-container">
            <DropDownIcon
              data-testid="ArrowDropDownIcon"
              className="resizable-doclist-icon-reverse"
            />
          </div>
        ) : (
          <div className="resizable-doclist-icon-container">
            <DropDownIcon
              color="red"
              data-testid="ArrowDropUpIcon"
              className="resizable-doclist-icon"
              aria-hidden="true"
            />
          </div>
        )}
        <Box
          data-testid="resizable-doclist-pull-bar"
          className="resizable-doclist-pull-bar"
        />
        {height === maxHeight ? (
          <div className="resizable-doclist-icon-container">
            <DropDownIcon
              data-testid="ArrowDropDownIcon"
              className="resizable-doclist-icon-reverse"
            />
          </div>
        ) : (
          <div className="resizable-doclist-icon-container">
            <DropDownIcon
              color="red"
              data-testid="ArrowDropUpIcon"
              className="resizable-doclist-icon"
              aria-hidden="true"
            />
          </div>
        )}
      </Box>
      <Box
        data-testid="resizable-doclist-content"
        className="resizable-doclist-content"
      >
        {children}
      </Box>
    </Box>
  );
};

export default ResizableDocList;
